import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../services/api";
import { ApiError } from "../../../services/api/BaseApi";
import JoinGatePrompt from "./JoinGatePrompt";
import styles from "./ListingCard.module.scss";
import type { BrowseListingView } from "../../../services/api/LfgApi";

interface ListingCardProps {
  listing: BrowseListingView;
  discordLinked?: boolean;
  onJoin?: () => void;
  onClick?: () => void;
}

const ListingCard: React.FC<ListingCardProps> = ({ listing, discordLinked = true, onJoin, onClick }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [discordGateError, setDiscordGateError] = useState(false);
  const [gatePromptOpen, setGatePromptOpen] = useState(false);
  const navigate = useNavigate();

  const hasSlots = listing.memberCount < listing.maxPlayers;

  const handleJoin = async () => {
    setLoading(true);
    setError(null);
    setDiscordGateError(false);
    try {
      await api.lobby.joinLobby(listing.lobbyId);
      onJoin?.();
    } catch (err: any) {
      if (err instanceof ApiError && err.status === 403 && err.data?.code === "DISCORD_NOT_LINKED") {
        setDiscordGateError(true);
      } else {
        setError(err?.message || "Failed to join lobby");
      }
    } finally {
      setLoading(false);
    }
  };

  const truncate = (str: string | undefined, len: number) => {
    if (!str) return "—";
    return str.length > len ? str.slice(0, len) + "…" : str;
  };

  return (
    <tr className={styles.row} onClick={onClick} style={{ cursor: "pointer" }}>
      <td className={styles.cell}>{truncate(listing.title, 30)}</td>
      <td className={styles.cell}>{listing.gameName || "—"}</td>
      <td className={`${styles.cell} ${styles.descCell}`}>{truncate(listing.description, 40)}</td>
      <td className={styles.cell}>{listing.hostUsername}</td>
      <td className={styles.cell}>{listing.memberCount}/{listing.maxPlayers}</td>
      <td className={styles.cell}>
        {hasSlots ? (
          <span
            className={styles.joinWrapper}
            title={!discordLinked ? "Link Discord to join" : undefined}
            onClick={(e) => {
              if (!discordLinked) {
                e.stopPropagation();
                setGatePromptOpen(true);
              }
            }}
          >
            <button
              className={styles.joinButton}
              onClick={(e) => { e.stopPropagation(); handleJoin(); }}
              disabled={loading || !discordLinked}
            >
              {loading ? "..." : "Join"}
            </button>
          </span>
        ) : (
          <span className={styles.fullBadge}>Full</span>
        )}
        {error && <span className={styles.error}>{error}</span>}
        {discordGateError && (
          <div className={styles.discordGateCallout} onClick={(e) => e.stopPropagation()}>
            <p className={styles.discordGateMessage}>
              You need to link your Discord account to join lobbies.
            </p>
            <div className={styles.discordGateActions}>
              <button
                className={styles.discordGateLink}
                onClick={() => navigate("/settings/linked-accounts")}
              >
                Link Discord
              </button>
              <button
                className={styles.discordGateDismiss}
                onClick={() => setDiscordGateError(false)}
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          </div>
        )}
        <JoinGatePrompt
          isOpen={gatePromptOpen}
          onClose={() => setGatePromptOpen(false)}
          onNavigateToSettings={() => {
            setGatePromptOpen(false);
            navigate("/settings/linked-accounts");
          }}
        />
      </td>
    </tr>
  );
};

export default ListingCard;
